import { useLazyQuery, useQuery } from "@apollo/client";
import { FC, useEffect, useMemo, useState } from "react";
import Container from "~/components/Container";
import Divider from "~/components/Divider";
import styles from "./MostPopular.module.scss";
import ProductItem from "~/components/Items/ProductItem";
import React from "react";
import useProductRelatedBySkus from "~/hooks/useProductRelatedBySkus";
import { RelatedPosition } from "~/constants/enum";
import { useRecoilValue } from "recoil";
import storeConfigAtom from "~/services/store-config/store-config.atom";
import {
  GET_PRODUCTS_TRACKING,
  QUERY_TRACK_VIEW_COLLECTION,
  QUERY_TRACK_VIEW_ITEM_RELATED,
  QUERY_TRACKING_IMPRESSION,
  QUERY_TRACKING_VIEW_RELATED,
} from "~/data/product/gql";
import useClient from "~/hooks/useClient";
import { Product } from "~/data/gallery-product/interface";

interface MostPopularProps {
  title?: string;
  mostPopularRelatedProductsBySkus?: any;
}

const MostPopular: FC<MostPopularProps> = ({
  title = "MOST POPULAR",
  mostPopularRelatedProductsBySkus,
}) => {
  const isClient = useClient();
  const { popular_group_id } = useRecoilValue(storeConfigAtom?.store);
  const [startFetching, setStartFetching] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);

  const [queryTrackViewItemRelated] = useLazyQuery(
    QUERY_TRACK_VIEW_ITEM_RELATED
  );

  const [queryTrackingViewList] = useLazyQuery(QUERY_TRACKING_VIEW_RELATED);
  const [queryTrackingViewCollection] = useLazyQuery(
    QUERY_TRACK_VIEW_COLLECTION
  );

  const [relatedTrackingImpression] = useLazyQuery(QUERY_TRACKING_IMPRESSION);

  const { products, relatedProductsBySkus } = useProductRelatedBySkus({
    ruleId: popular_group_id,
    limit: 4,
    position: RelatedPosition.HomePage,
    skip: !startFetching,
  });

  const { data: dataCollectionTracking, loading: loadingCollectionTracking } =
    useQuery(GET_PRODUCTS_TRACKING, {
      variables: {
        skus: mostPopularRelatedProductsBySkus?.items?.map(
          (product: Product) => product.sku + ""
        ),
      },
      context: { fetchOptions: { method: "GET" } },
      skip:
        !mostPopularRelatedProductsBySkus?.items ||
        !mostPopularRelatedProductsBySkus?.items.length,
    });

  const _list = useMemo(() => {
    if (
      (loadingCollectionTracking || !dataCollectionTracking) &&
      !!mostPopularRelatedProductsBySkus?.items
    )
      return mostPopularRelatedProductsBySkus?.items;
    if (
      !!mostPopularRelatedProductsBySkus?.items &&
      mostPopularRelatedProductsBySkus?.items.length
    )
      return mostPopularRelatedProductsBySkus?.items?.map(
        (product: Product) => {
          const tracking = dataCollectionTracking?.productsBySkus?.items?.find(
            (item: any) => item.sku === product.sku
          );
          return {
            ...product,
            ...tracking,
          };
        }
      );
    return (products ?? []).map((product: Product) => {
      const tracking = dataCollectionTracking?.productsBySkus?.items?.find(
        (item: any) => item.sku === product.sku
      );
      return {
        ...product,
        ...tracking,
      };
    });
  }, [
    loadingCollectionTracking,
    dataCollectionTracking,
    mostPopularRelatedProductsBySkus,
  ]);

  useEffect(() => {
    const ProductsSliderElement = document.getElementById(
      popular_group_id + ""
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // onEnter?.();
            setStartFetching(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.25,
        rootMargin: "0px 0px 200px 0px",
      }
    );
    const observer2 = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsIntersecting(true);
            observer2.disconnect();
          }
        });
      },
      {
        threshold: 0, // when viewport is 0% visible
        rootMargin: "0px 0px 10px 0px", // margin around the root
      }
    );

    ProductsSliderElement && observer.observe(ProductsSliderElement);
    ProductsSliderElement && observer2.observe(ProductsSliderElement);

    return () => {
      ProductsSliderElement && observer.unobserve(ProductsSliderElement);
      ProductsSliderElement && observer2.unobserve(ProductsSliderElement);
    };
  }, []);

  useEffect(() => {
    if (!!isIntersecting && products?.length > 0) {
      console.log("handleImpression", popular_group_id, products);
      queryTrackViewItemRelated({
        variables: {
          block_id: popular_group_id,
          skus: products?.map((prod: any) => prod?.sku),
        },
      });
      relatedTrackingImpression({
        variables: {
          skus: products?.map((prod: any) => prod?.sku),
          block_id: popular_group_id,
          type: "related",
          search_query: "",
        },
      });
      queryTrackingViewList({
        variables: {
          block_id: popular_group_id,
        },
      });
      queryTrackingViewCollection({
        variables: {
          block_id: popular_group_id,
          type: "related",
        },
      });
    }
  }, [products, isIntersecting]);

  // if (!products?.length && !loading && !!startFetching) {
  //     return null;
  // }

  return (
    <div className="overflow-hidden" id={popular_group_id + ""}>
      <div className={styles.most_popular_wrapper}>
        <img
          className={styles.rain_pattern}
          src="/images/rain_top_left.png"
          alt="Rain pattern"
          width={408}
          height={308}
        />
        <img
          className={styles.rain_pattern}
          src="/images/rain_top_right.png"
          alt="Rain pattern"
          width={407}
          height={360}
        />
        <img
          className={styles.rain_pattern}
          src="/images/rain_bottom_left.png"
          alt="Rain pattern"
          width={233}
          height={240}
        />
        <img
          className={styles.rain_pattern}
          src="/images/rain_bottom_right.png"
          alt="Rain pattern"
          width={233}
          height={240}
        />

        <Container className={styles.most_popular}>
          <Divider
            text={
              relatedProductsBySkus?.block_title ??
              mostPopularRelatedProductsBySkus?.block_title ??
              title
            }
            className={"text-white"}
          />
          <div className="gap-6 grid grid-cols-2 lg:grid-cols-4 w-full ">
            {_list?.map((item: any) => (
              <React.Fragment key={item.sku}>
                <ProductItem
                  nameProps={{
                    className: "text-white !font-bold",
                  }}
                  data={item}
                  params={{
                    b: popular_group_id,
                  }}
                />
              </React.Fragment>
            ))}
          </div>
        </Container>
      </div>
    </div>
  );
};

export default MostPopular;
